import { Component, HostBinding, OnInit, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SeoMetadataService } from '@shared/services/seo-metadata.service';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '@shared/redux/config-store-state.model';
import { emitAlert, updateUser } from '@shared/redux/actions/actions-store-state.model';
import { FunctionsService } from '@shared/services/functions.service';
import { AuthFirebaseService } from '@shared/services/auth-firebase.service';
import { RegisterApiService } from '@modules/register/registe-api.service';
import { JWT_TOKEN } from '@shared/data-structure/jwt-token';

@Component({
  selector: 'astrea-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {

  @HostBinding('class') public hostClass: string = 'h-100';

  public formRegister = this.formBuilder.group({
    name: ['',  Validators.required],
    user: ['', [Validators.required, Validators.email]],
    confirmUser: ['', [Validators.required, Validators.email]],
    newPass: ['', [Validators.required, Validators.minLength(10), FunctionsService.validatePassword]],
    confirmPass: ['',  [Validators.required, Validators.minLength(10), FunctionsService.validatePassword]],
    terms: [false, Validators.required]
  }, {
    validators: [ FunctionsService.matchPasswords, FunctionsService.matchUser ]
  });
  public hide = true;
  public submitted = false;

  public loading: boolean = false;
  public redirect = '/home';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private formBuilder: UntypedFormBuilder,
    private api: RegisterApiService,
    private authFirebase: AuthFirebaseService,
    private seo: SeoMetadataService,
    private _t: TranslateService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) { }

  ngOnInit(): void {
    this.setMetadata();
    this.route.queryParams
      .subscribe({
        next: (params: Params) => {
          this.redirect = params.redirect ? params.redirect : '/home';
        }
      });
  }

  setMetadata(): void {
    this.seo.setMetadata({
      title: 'Crear una cuenta',
      description: 'Creá una cuenta para acceder a los servicios de la Editorial Astrea.',
      relativeUrl: '/register'
    });
  }

  async registerUser() {
    this.submitted = true;
    if (this.formRegister.valid) {
      this.loading = true;

      const email = this.formRegister.get('user')!.value;
      const pass = this.formRegister.get('newPass')!.value;

      // STEP 1: Creamos usuarios en firebase
      const firebaseUser = await this.authFirebase.createUser(email, pass)
        .catch(err => {
          this.loading = false;
          this.dispatchAlertError();
          return false;
        });


      // Guardamos el token para que no falle la primera request
      if (firebaseUser.user!._delegate!.accessToken) {
        if (this.isBrowser)
          localStorage.setItem(JWT_TOKEN, firebaseUser.user!._delegate!.accessToken);
      }


      // STEP 2: Cremos usuario en el backend
      const idFirebase = firebaseUser.user!.uid;
      const user = await this.api.postUserAV({ email, idFirebase, firstname: this.formRegister.get('name')!.value })
        .catch(err => {
          this.loading = false;
          this.dispatchAlertError();
          return false;
        });


      // STEP 3: Guardamos el usuario en Redux, la sesión del usuario queda logueada
      this.store.dispatch(updateUser({ user: user.result.data }));

      this.loading = false;
      this.dispatchAlertOK();
      setTimeout(() => {
        this.router.navigate([this.redirect]);
      }, 0);
    }
  }

  changeTypePass(): void {
    this.hide = !this.hide;
  }

  get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }


  /* ********** ALERTS ********** */

  dispatchAlertOK(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.welcome'),
      alertType: 'success'
    }));
  }

  dispatchAlertError(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.register_err'),
      alertType: 'danger'
    }));
  }

}
